import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { Label } from "layout/typography/Label";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useGetSelectableReportsQuery } from "store/queries/report";

// Per-report access for hospital-staff users. Always shown. The selectable set is scoped to the
// accounts currently chosen on the form, so access can never exceed the user's account
// assignment. Leaving it empty means "no restriction" (the user sees every report in their
// accounts); selecting reports narrows them to exactly those.
export function ReportAccess() {
  const { control, watch, getValues, setValue } = useFormContext();

  const accountIds: string[] = watch("accountIds") || [];

  const { data, isFetching } = useGetSelectableReportsQuery(
    { accountIds },
    { skip: accountIds.length === 0 }
  );

  // Reconcile the saved grants against what is actually selectable (published, in-account
  // reports). A previously granted report that is no longer offered — e.g. it became unpublished
  // — would otherwise render as a blank chip. Drop those once the candidate list has loaded so
  // the selection only ever holds valid, displayable reports.
  useEffect(() => {
    if (isFetching || !data || accountIds.length === 0) return;
    const validIds = new Set((data.data || []).map((r) => r.id));
    const current: string[] = getValues("reportAccessIds") || [];
    const pruned = current.filter((id) => validIds.has(id));
    if (pruned.length !== current.length) {
      setValue("reportAccessIds", pruned);
    }
  }, [data, isFetching, accountIds, getValues, setValue]);

  const options = useMemo(() => {
    return (data?.data || []).map((report) => {
      const payout = report.stripePayoutAmountInCents
        ? parsePrice(centsToDollars(report.stripePayoutAmountInCents))
        : null;
      // Mirror the Reports tab "Account/Provider Name" column: report name, else provider name.
      const accountProviderName =
        report.name || report.providerName || "Report";
      const label = [accountProviderName, parseDate(report.reportDate), payout]
        .filter(Boolean)
        .join(" — ");

      return { label, value: report.id };
    });
  }, [data]);

  return (
    <div className="formgrid grid w-100">
      <div className="field p-fluid col-12">
        <Label htmlFor="reportAccessIds">Accessible Reports</Label>
        <Controller
          name="reportAccessIds"
          control={control}
          render={({ field: { onChange, value } }) => (
            <MultiSelect
              filter
              display="chip"
              id="reportAccessIds"
              className="w-full"
              optionLabel="label"
              optionValue="value"
              options={options}
              value={value || []}
              onChange={(e) => onChange(e.value)}
              disabled={accountIds.length === 0}
              placeholder={
                isFetching
                  ? "Loading reports..."
                  : "All reports (no restriction)"
              }
              maxSelectedLabels={3}
            />
          )}
        />
        <small className="text-color-secondary">
          {accountIds.length === 0
            ? "Select one or more accounts above to choose reports."
            : "Leave empty to allow access to all reports in the selected accounts."}
        </small>
      </div>
    </div>
  );
}
